import { Plus, Trash2 } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { ServerError } from '@/shared/ui/server-error';

import { toPutRatesRequest } from '../api/mappers';
import { usePutExchangeRates } from '../hooks/use-put-exchange-rates';
import {
  emptyRateRow,
  rateRowsSchema,
  type RateRowInput,
  type RateRowsInput,
  type RateRowsOutput,
} from '../schemas/rate-rows-schema';

export interface UpsertRatesDialogProps {
  /** Rows to start from; null closes the dialog. A row action passes the pair it was clicked on. */
  rows: readonly RateRowInput[] | null;
  onClose: () => void;
}

export function UpsertRatesDialog({ rows, onClose }: UpsertRatesDialogProps) {
  return (
    <Dialog
      open={rows !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-lg">
        {rows ? <RatesForm key={rows.map(rowKey).join('|')} rows={rows} onDone={onClose} /> : null}
      </DialogContent>
    </Dialog>
  );
}

const rowKey = (row: RateRowInput) => `${row.baseCurrency}/${row.quoteCurrency}/${row.rate}`;

const FIELD_KEYS = ['baseCurrency', 'quoteCurrency', 'rate'] as const;

function RatesForm({ rows, onDone }: { rows: readonly RateRowInput[]; onDone: () => void }) {
  const { t } = useTranslation(['exchangeRates', 'common']);
  const put = usePutExchangeRates();
  const form = useForm<RateRowsInput, unknown, RateRowsOutput>({
    resolver: useI18nResolver(rateRowsSchema),
    defaultValues: { rates: rows.length > 0 ? [...rows] : [emptyRateRow()] },
  });
  const list = useFieldArray({ control: form.control, name: 'rates' });
  const applyFieldErrors = useApplyFieldErrors(form.setError);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await put.mutateAsync(toPutRatesRequest(values));
      onDone();
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  const ratesError = form.formState.errors.rates;
  const listMessage = ratesError?.root?.message ?? ratesError?.message;
  const showServerError = put.isError && !put.error.hasFieldErrors;

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <DialogHeader>
          <DialogTitle>{t('exchangeRates:dialog.title')}</DialogTitle>
          <DialogDescription>{t('exchangeRates:dialog.description')}</DialogDescription>
        </DialogHeader>
        {showServerError ? (
          <ServerError
            error={put.error}
            message={put.error.kind === 'forbidden' ? t('exchangeRates:errors.managersOnly') : null}
          />
        ) : null}
        <div className="flex flex-col gap-2">
          <div
            className="text-muted-foreground grid grid-cols-[1fr_1fr_1.5fr_2rem] gap-2 px-0.5 text-xs font-medium"
            aria-hidden
          >
            {FIELD_KEYS.map((key) => (
              <span key={key}>{t(`exchangeRates:fields.${key}`)}</span>
            ))}
          </div>
          {list.fields.map((row, index) => (
            <div key={row.id} className="grid grid-cols-[1fr_1fr_1.5fr_2rem] items-start gap-2">
              {FIELD_KEYS.map((key) => (
                <FormField
                  key={key}
                  control={form.control}
                  name={`rates.${index}.${key}`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="sr-only">
                        {t('exchangeRates:fields.rowField', {
                          field: t(`exchangeRates:fields.${key}`),
                          row: index + 1,
                        })}
                      </FormLabel>
                      <FormControl>
                        {key === 'rate' ? (
                          <Input
                            inputMode="decimal"
                            autoComplete="off"
                            placeholder="0.9234"
                            className="font-mono tabular-nums"
                            {...field}
                          />
                        ) : (
                          <Input
                            maxLength={3}
                            autoComplete="off"
                            autoCapitalize="characters"
                            spellCheck={false}
                            placeholder={key === 'baseCurrency' ? 'USD' : 'EUR'}
                            className="font-mono uppercase"
                            {...field}
                            onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="mt-0.5"
                aria-label={t('exchangeRates:dialog.removeRow', { row: index + 1 })}
                disabled={list.fields.length === 1}
                onClick={() => list.remove(index)}
              >
                <Trash2 aria-hidden />
              </Button>
            </div>
          ))}
          {listMessage ? (
            <p role="alert" className="text-destructive text-sm">
              {listMessage}
            </p>
          ) : null}
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => list.append(emptyRateRow(form.getValues('rates.0.baseCurrency')))}
            >
              <Plus aria-hidden />
              {t('exchangeRates:dialog.addRow')}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onDone} disabled={put.isPending}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={put.isPending}>
            {put.isPending
              ? t('exchangeRates:dialog.submitting')
              : t('exchangeRates:dialog.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
